import {
  type AnchorHTMLAttributes,
  type ComponentType,
  createContext,
  type ReactNode,
  type Ref,
  useContext,
} from 'react'

/**
 * Router-agnostic links. `@fruuit/ui` never imports a router; the app registers its router's
 * link component once with `<LinkProvider component={...}>`, and every internal link in the
 * library (breadcrumbs, stat tiles, nav) renders through it. Without a provider it falls back
 * to a plain `<a href>`.
 */
export interface UiLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  href: string
  ref?: Ref<HTMLAnchorElement>
}

function AnchorLink({ href, ...rest }: UiLinkProps) {
  return <a href={href} {...rest} />
}

const LinkContext = createContext<ComponentType<UiLinkProps>>(AnchorLink)

export function LinkProvider({
  component,
  children,
}: {
  component: ComponentType<UiLinkProps>
  children: ReactNode
}) {
  return <LinkContext value={component}>{children}</LinkContext>
}

export function UiLink(props: UiLinkProps) {
  const Component = useContext(LinkContext)
  return <Component {...props} />
}
